<template>
  <SheetModal
    class="NewCrewModalView"
    data-theme="light"
    fit="hug"
    aria-label="Start a crew"
    :aria-busy="isSaving"
    @close="$emit('close')"
  >
    <form
      class="NewCrewModalView__form"
      @submit.prevent="handleSubmit"
    >
      <fieldset
        class="NewCrewModalView__fieldset"
        :disabled="isSaving"
      >
        <SheetHeader
          title="Start a crew"
          @cancel="$emit('close')"
        />

        <div class="NewCrewModalView__fieldGrid">
          <FormField label="Crew name">
            <FormInput
              v-model="form.name"
              name="crewName"
              autocomplete="off"
              :placeholder="namePlaceholder"
              isRequired
              shouldAutoFocus
            />
          </FormField>

          <FormField label="Location">
            <FormInput
              v-model="form.location"
              name="location"
              autocomplete="address-level2"
              :placeholder="locationPlaceholder"
            />
          </FormField>
        </div>
      </fieldset>
    </form>
  </SheetModal>
</template>

<script lang="ts" setup>
import FormField from "@/components/FormField.vue";
import FormInput from "@/components/FormInput.vue";
import SheetHeader from "@/components/SheetHeader.vue";
import SheetModal from "@/components/SheetModal.vue";
import getRouteForCrew from "@/lib/utils/route/getRouteForCrew";
import useAuthStore from "@/stores/useAuthStore";
import useCrewStore from "@/stores/useCrewStore";
import useModalStore from "@/stores/useModalStore";
import { courtMap } from "@shared/lib/constants/courts";
import { themeMap } from "@shared/lib/constants/themes";
import { storeToRefs } from "pinia";
import { computed, ref } from "vue";

defineEmits<{
  close: [],
}>();

const {
  createCrew,
} = useCrewStore();

const {
  replaceModal,
} = useModalStore();

const {
  currentPlayer,
  theme,
} = storeToRefs(useAuthStore());

const isSaving = ref(false);

const form = ref<{
  name: string,
  location: string,
}>({
  name: "",
  location: "",
});

const namePlaceholder = computed(() => {
  if (!theme.value) return;

  return themeMap.get(theme.value)?.name;
});

const locationPlaceholder = computed(() => {
  if (!theme.value) return;

  return courtMap.get(theme.value)?.location;
});

async function handleSubmit () {
  const name = form.value.name.trim();
  if (!name) return;

  isSaving.value = true;

  try {
    const crew = await createCrew({
      name,
      location: form.value.location.trim() || undefined,
    });

    if (currentPlayer.value) {
      currentPlayer.value.crew_memberships.push({
        crew_id: crew.id,
        player_id: currentPlayer.value.id,
        role: "commissioner",
        crew,
      });
    }

    replaceModal(getRouteForCrew(crew.id));
  } catch (error) {
    alert((error as Error).message);
  } finally {
    isSaving.value = false;
  }
}
</script>

<style lang="scss" scoped>
.NewCrewModalView__fieldset {
  display: grid;
  align-content: start;
  height: 100%;
}

.NewCrewModalView__fieldGrid {
  display: grid;
  gap: 24px;
  padding: 24px 16px calc(24px + var(--safeAreaInsetBottom));
}
</style>
